import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Camera, Upload, Loader2, Smartphone, Monitor, X, CheckCircle2 } from 'lucide-react';
import { regCfg, shrinkImage, uploadError } from './regSession';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const FAM = `${API_URL}/api/reg-family`;

// Phones and tablets get the camera straight from the file picker; computers get
// the hand-off dialog (QR → phone, or the computer's own webcam). Same pattern as
// BOP's ScanCaptureDialog.
const isTouch = () => typeof window !== 'undefined'
  && (window.matchMedia?.('(pointer: coarse)').matches || /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || ''));

const fileName = (v) => String(v || '').split(' (')[0];

/**
 * A photo/file answer (e.g. birth certificate). "Take photo" + "Upload file";
 * whatever arrives is stored privately and attached to the answer automatically.
 * `preview` = builder preview: buttons show, nothing is uploaded.
 */
export default function RegPhotoField({ orgId, formId, question, value, onChange, brand, preview, forLabel }) {
  const camRef = useRef(null);
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [handoff, setHandoff] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (preview) { onChange(`${file.name} (preview — not uploaded)`); return; }
    setBusy(true); setErr('');
    try {
      const fd = new FormData(); fd.append('file', await shrinkImage(file));
      const { data } = await axios.post(`${API_URL}/api/reg-submissions/${orgId}/public/forms/${formId}/upload`, fd, regCfg());
      onChange(data.answer);
    } catch (e) { setErr(uploadError(e)); }
    finally { setBusy(false); if (camRef.current) camRef.current.value = ''; if (fileRef.current) fileRef.current.value = ''; }
  };

  const takePhoto = () => {
    if (isTouch() || preview) camRef.current?.click();
    else setHandoff(true);
  };

  const btn = 'inline-flex items-center justify-center gap-2 rounded-lg border px-4 h-11 text-sm font-medium dark:border-slate-600';
  return (
    <div className="space-y-2" data-testid={`photo-${question.id}`}>
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => upload(e.target.files?.[0])} data-testid={`photo-camera-${question.id}`} />
      <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={e => upload(e.target.files?.[0])} data-testid={`input-${question.id}`} />
      {value ? (
        <div className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-300" data-testid={`photo-attached-${question.id}`}>
          <CheckCircle2 className="h-4 w-4 shrink-0" /><span className="truncate flex-1">Attached: {fileName(value)}</span>
          <button type="button" className="underline text-xs" onClick={() => onChange('')}>Replace</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button type="button" className={`${btn} text-white border-transparent`} style={{ background: brand }} onClick={takePhoto} disabled={busy} data-testid={`photo-take-${question.id}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} Take photo
          </button>
          <button type="button" className={btn} onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`photo-upload-${question.id}`}>
            <Upload className="h-4 w-4" /> Upload a file
          </button>
        </div>
      )}
      {err && <p className="text-xs text-red-600">{err}</p>}
      {handoff && (
        <HandoffDialog orgId={orgId} formId={formId} question={question} brand={brand} forLabel={forLabel}
          onClose={() => setHandoff(false)} onAnswer={(a) => { onChange(a); setHandoff(false); }} onFile={(f) => { setHandoff(false); upload(f); }} />
      )}
    </div>
  );
}

function HandoffDialog({ orgId, formId, question, brand, forLabel, onClose, onAnswer, onFile }) {
  const [token, setToken] = useState('');
  const [err, setErr] = useState('');
  const [cam, setCam] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const answerRef = useRef(onAnswer);
  answerRef.current = onAnswer;

  useEffect(() => {
    axios.post(`${FAM}/${orgId}/forms/${formId}/scan`, { question_id: question.id, for_label: forLabel || '' }, regCfg())
      .then(r => setToken(r.data.token))
      .catch(e => setErr(e?.response?.data?.detail || 'Could not make a phone link'));
  }, [orgId, formId, question.id, forLabel]);

  // Phone → computer: poll until the photo arrives.
  useEffect(() => {
    if (!token) return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await axios.get(`${FAM}/${orgId}/forms/${formId}/scan/${token}`, regCfg());
        if (data.answer) { clearInterval(t); answerRef.current(data.answer); }
      } catch { /* keep waiting */ }
    }, 2500);
    return () => clearInterval(t);
  }, [token, orgId, formId]);

  const stopCam = () => { if (cam) cam.getTracks().forEach(x => x.stop()); setCam(null); };
  useEffect(() => () => { if (cam) cam.getTracks().forEach(x => x.stop()); }, [cam]);
  const startCam = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } } });
      setCam(s);
      setTimeout(() => { if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.play(); } }, 50);
    } catch { setErr("This computer's camera isn't available — use your phone instead."); }
  };
  const shoot = () => {
    const v = videoRef.current, c = canvasRef.current; if (!v || !c) return;
    c.width = v.videoWidth; c.height = v.videoHeight; c.getContext('2d').drawImage(v, 0, 0);
    c.toBlob(b => { if (!b) return; stopCam(); onFile(new File([b], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' })); }, 'image/jpeg', 0.9);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => { stopCam(); onClose(); }} data-testid="photo-handoff">
      <div className="w-full max-w-lg rounded-xl bg-white dark:bg-slate-800 p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div><h3 className="font-bold">Take a photo of the {question.label?.toLowerCase() || 'document'}</h3>
            <p className="text-xs text-slate-500">Lay it flat in good light. It attaches to this registration automatically.</p></div>
          <button onClick={() => { stopCam(); onClose(); }} aria-label="Close"><X className="h-5 w-5" /></button>
        </div>
        {cam ? (
          <div className="space-y-3">
            <div className="relative bg-black rounded-lg overflow-hidden aspect-[4/3]">
              <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
              <canvas ref={canvasRef} className="hidden" />
              <div className="absolute inset-4 border-2 border-white/60 border-dashed rounded-lg pointer-events-none" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button className="h-11 rounded-lg border" onClick={stopCam}>Back</button>
              <button className="h-11 rounded-lg text-white font-medium" style={{ background: brand }} onClick={shoot} data-testid="photo-webcam-shoot"><Camera className="h-4 w-4 inline mr-1" /> Capture</button>
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="flex flex-col items-center gap-2 border rounded-lg p-3 dark:border-slate-600" data-testid="photo-qr">
              <div className="text-sm font-medium flex items-center gap-1"><Smartphone className="h-4 w-4" /> Use your phone</div>
              {token ? <img src={`${FAM}/scan/${token}/qr.png`} alt="QR code — scan with your phone camera" className="w-40 h-40" data-testid="photo-qr-img" />
                : err ? <p className="text-xs text-red-600 text-center">{err}</p>
                  : <Loader2 className="h-6 w-6 animate-spin my-16 text-slate-400" />}
              <p className="text-[11px] text-slate-500 text-center">Scan with your phone's camera, take the photo there — it shows up here.</p>
            </div>
            <div className="flex flex-col items-center justify-center gap-2 border rounded-lg p-3 dark:border-slate-600">
              <div className="text-sm font-medium flex items-center gap-1"><Monitor className="h-4 w-4" /> This computer</div>
              <button className="h-11 px-4 rounded-lg border" onClick={startCam} data-testid="photo-use-webcam"><Camera className="h-4 w-4 inline mr-1" /> Use this camera</button>
              <p className="text-[11px] text-slate-500 text-center">Best if this computer has a webcam.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
